import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  ArrowLeft, 
  Download, 
  ZoomIn, 
  ZoomOut, 
  ChevronLeft, 
  ChevronRight, 
  Send, 
  Copy, 
  CheckCircle, 
  Share2,
  FileText,
  FileSignature
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { documentService } from '../services/documentService';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

// Update worker URL to use the correct version from pdfjs-dist
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`;

const DocumentPreviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [document, setDocument] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [shareStatus, setShareStatus] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  

  useEffect(() => {
    const loadDocument = async () => {
      try {
        const documentData = await documentService.getDocument(id);
        setDocument(documentData);
        
        // Fetch the file as a blob to avoid CORS issues
        try {
          console.log(`Fetching document file from: ${API_URL}/documents/${id}/file`);
          const response = await axios.get(`${API_URL}/documents/${id}/file`, {
            responseType: 'blob',
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          });
          
          console.log("File fetch successful, content type:", response.headers['content-type']);
          
          // Get the correct MIME type from response if available
          const contentType = response.headers['content-type'] || 'application/pdf';
          const fileBlob = new Blob([response.data], { type: contentType });
          const fileObjectUrl = URL.createObjectURL(fileBlob);
          setPdfBlob(fileBlob);
          setFileUrl(fileObjectUrl);
          setPdfError(false);
        } catch (fileError) {
          console.error('Error fetching file:', fileError);
          // Log more detailed error information
          if (fileError.response) {
            console.error('Server returned error status:', fileError.response.status);
            console.error('Server error details:', fileError.response.data);
          }
          setPdfError(true);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError(err.response?.data?.error || 'Failed to load document');
        setLoading(false);
      }
    };
    
    loadDocument();
    
    // Cleanup object URL on unmount
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [id, user.token]);
  
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const previousPage = () => {
    setPageNumber(prevPageNumber => Math.max(prevPageNumber - 1, 1));
  };

  const nextPage = () => {
    setPageNumber(prevPageNumber => Math.min(prevPageNumber + 1, numPages));
  };

  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 3));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  };
  
  const handleShareDocument = async () => {
    if (!recipientEmail) {
      setShareStatus('Please enter a recipient email');
      return;
    }
    
    try {
      setShareStatus('Sharing document...');
      
      // Call the API to share the document
      await axios.post(`${API_URL}/share`, {
        documentId: id,
        email: recipientEmail.toLowerCase(),
        name: recipientName || recipientEmail.split('@')[0],
        message: shareMessage,
        permission: sharePermission
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      setShareStatus('Document shared successfully!');
      
      // Reset form fields
      setRecipientEmail('');
      setRecipientName('');
      setShareMessage('');
      
      // Close share panel after a delay
      setTimeout(() => {
        setShowSharePanel(false);
        setShareStatus('');
      }, 2000);
      
    } catch (error) {
      console.error('Error sharing document:', error);
      setShareStatus(error.response?.data?.error || 'Failed to share document');
    }
  };
  
  const copyShareLink = () => {
    const shareLink = `${window.location.origin}/view/${id}`;
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
      });
  };
  
  const handleDownload = async () => {
    try {
      console.log('📄 Downloading document with embedded signatures...');
      
      const token = localStorage.getItem('token');
      let fileUrl = `${API_URL}/documents/${id}/download`;
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch the file as a blob
      const response = await axios.get(fileUrl, {
        responseType: 'blob',
        headers
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a temporary download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = document?.originalFileName || document?.name || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
      
      console.log('✅ Document downloaded successfully');
      
    } catch (error) {
      console.error('❌ Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    console.error('Error loading document:', error);
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center max-w-md">
          <div className="text-red-500 text-xl mb-4">Error</div>
          <p className="text-gray-700 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 text-gray-800">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 shadow-sm sticky top-0 z-10">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard')}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{document?.name}</h1>
                <div className="text-sm text-gray-500">Preview Mode</div>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button 
                onClick={() => navigate(`/document/${id}`)}
                className="px-5 py-2.5 bg-green-600 text-white rounded-md flex items-center mr-2 hover:bg-green-700 transition-colors font-medium shadow-md"
              >
                <FileSignature size={18} className="mr-2" />
                Sign Document
              </button>
              <button 
                onClick={() => setShowSharePanel(!showSharePanel)}
                className={`px-4 py-2 rounded-md transition flex items-center ${
                  showSharePanel 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Share2 size={18} className="mr-2" />
                Share Document
              </button>
              
              <button
                onClick={handleDownload}
                disabled={!document}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                title="Download document with embedded signatures"
              >
                <Download size={20} className="text-gray-700" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`md:col-span-${showSharePanel ? '2' : '3'}`}>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-4">
              <div className="bg-gray-100 rounded-md p-4 mb-4 overflow-auto max-h-[70vh]">
                {/* Sign document banner at the top */}
                <div className="bg-green-50 border border-green-100 rounded-md p-3 mb-4 flex justify-between items-center">
                  <div className="flex items-center">
                    <FileSignature size={24} className="text-green-600 mr-3" />
                    <div>
                      <h3 className="font-medium text-green-800">Ready to sign?</h3>
                      <p className="text-sm text-green-700">Open the full document to add your signature</p>
                    </div>
                  </div>
                  {/* <button 
                    onClick={() => navigate(`/document/${id}`)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium text-sm shadow-sm"
                  >
                    Sign Now
                  </button> */}
                </div>
                
                {pdfError ? (
                  <div className="text-center py-10">
                    <div className="text-red-500 text-xl mb-4">Failed to load PDF</div>
                    <div className="p-4 bg-gray-200 rounded-lg mb-6 max-w-md mx-auto">
                      <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-700 mb-3">The document could not be loaded due to a server error (500).</p>
                      <div className="text-left text-sm text-gray-600 bg-gray-100 p-3 rounded-md mb-3">
                        <p className="font-medium mb-1">Troubleshooting:</p>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>The file may be corrupted or in an unsupported format</li>
                          <li>The backend server may have issues processing this file</li>
                          <li>The file may be too large or have permission issues</li>
                          <li>Check the server logs for more details</li>
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-center space-x-3">
                      <button 
                        onClick={handleDownload}
                        disabled={!document}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50"
                      >
                        Download with signatures
                      </button>
                      <button 
                        onClick={() => navigate('/dashboard')}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                      >
                        Back to Dashboard
                      </button>
                    </div>
                  </div>
                ) : fileUrl ? (
                  <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) => {
                      console.error('Error loading PDF:', error);
                      setPdfError(true);
                    }}
                    loading={
                      <div className="flex justify-center items-center h-96">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    }
                    error={
                      <div className="text-center py-10">
                        <p className="text-red-500 mb-2">Failed to load PDF</p>
                        <p className="text-gray-600 text-sm">
                          Please try downloading the document instead
                        </p>
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={pageNumber} 
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      scale={scale}
                      className="mx-auto shadow-md"
                    />
                  </Document>
                ) : (
                  <div className="flex justify-center items-center h-96">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                )}
              </div>
              
              {numPages && !pdfError && (
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={zoomOut}
                      disabled={scale <= 0.5}
                      className="p-2 bg-gray-100 rounded-md disabled:opacity-50 hover:bg-gray-200"
                      title="Zoom out"
                    >
                      <ZoomOut size={18} className="text-gray-700" />
                    </button>
                    <span className="text-sm text-gray-600">{Math.round(scale * 100)}%</span>
                    <button
                      onClick={zoomIn}
                      disabled={scale >= 3}
                      className="p-2 bg-gray-100 rounded-md disabled:opacity-50 hover:bg-gray-200"
                      title="Zoom in"
                    >
                      <ZoomIn size={18} className="text-gray-700" />
                    </button>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={previousPage}
                      disabled={pageNumber <= 1}
                      className="p-2 bg-gray-100 rounded-md disabled:opacity-50 hover:bg-gray-200 flex items-center"
                    >
                      <ChevronLeft size={18} className="text-gray-700" />
                      <span className="ml-1 text-gray-700">Previous</span>
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {pageNumber} of {numPages}
                    </span>
                    <button
                      onClick={nextPage}
                      disabled={pageNumber >= numPages}
                      className="p-2 bg-gray-100 rounded-md disabled:opacity-50 hover:bg-gray-200 flex items-center"
                    >
                      <span className="mr-1 text-gray-700">Next</span>
                      <ChevronRight size={18} className="text-gray-700" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Share panel */}
          {showSharePanel && (
            <div className="md:col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm mb-4">
                <h2 className="text-lg font-bold mb-4">Share Document</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                    <input 
                      type="email"
                      placeholder="email@example.com"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name (Optional)</label>
                    <input 
                      type="text"
                      placeholder="John Doe"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Permission Level</label>
                    <select
                      value={sharePermission}
                      onChange={(e) => setSharePermission(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="view">Can view</option>
                      <option value="comment">Can comment</option>
                      <option value="sign">Can sign</option>
                      <option value="edit">Can edit</option>
                      <option value="share">Can share</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                    <textarea
                      value={shareMessage}
                      onChange={(e) => setShareMessage(e.target.value)}
                      placeholder="Please review this document..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                  </div>
                  
                  <button
                    onClick={handleShareDocument}
                    className="w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center justify-center"
                  >
                    <Send size={16} className="mr-2" />
                    Share Document
                  </button>
                  
                  {shareStatus && (
                    <div className={`text-sm p-2 rounded ${
                      shareStatus.includes('successfully') 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {shareStatus}
                    </div>
                  )}
                  
                  <div className="relative mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Or share via link</h3>
                    <div className="flex">
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}/view/${id}`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-gray-600"
                      />
                      <button
                        onClick={copyShareLink}
                        className="px-3 py-2 bg-gray-100 border border-gray-300 border-l-0 rounded-r-md hover:bg-gray-200 transition flex items-center"
                      >
                        {linkCopied ? (
                          <CheckCircle size={16} className="text-green-600" />
                        ) : (
                          <Copy size={16} className="text-gray-600" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Anyone with the link can view this document
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewPage; 